import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment';
import { map, Observable } from 'rxjs';
import { CoreListEnvelop, CoreListEnvelopExtras, Revision } from '@mkp/shared/models';
import { QueryHeaders } from '../query-params.model';
import { ListRequestConfig, ListRequestQuery, RequestConfig } from './core-api.request';

const CORE_API_URL = new InjectionToken('Core API Base Url', {
  factory: () => inject(ENVIRONMENT_PROVIDER).api.core,
});

export const CORE_HIGH_PAGE = 400;
export const CORE_DEFAULT_PAGE = 10;
export type CoreQueryConfig = ListRequestConfig & { id: string | number };
export type CoreListQueryConfig = Omit<CoreQueryConfig, 'id'>;
export type CoreApiListResponseKeys<Dto, Model = Dto> = keyof CoreApiResponse<Dto, Model>;
export type CoreApiListResponseReturnType<
  Dto,
  K extends CoreApiListResponseKeys<Dto, Model>,
  Model = Dto,
> = CoreApiResponse<Dto, Model>[K];
export type CoreApiListMapper<Dto, Model> = (dto: Dto[]) => Model[];
export type CoreApiMapper<Dto, Model> = (dto: Dto) => Model;
export type CoreApiResponse<Dto, Model = Dto> = {
  results: Dto[] | Model[];
  totalCount: number;
  links: CoreListEnvelopExtras<Dto>['_links'];
  limit: number;
  offset: number;
  sort: string;
  pagination: {
    filter: string;
    sort: string;
    limit: number;
    offset: number;
  };
};

const createCoreApiResponse = <Dto, Model = Dto>(
  response: CoreListEnvelop<Dto>,
  mapper?: (dto: Dto[]) => Model[]
): CoreApiResponse<Dto, Model> => {
  const results = response._embedded.results;
  const mapped = mapper ? mapper(results) : results;

  return {
    results: mapped,
    totalCount: response.totalCount,
    limit: response.limit,
    offset: response.offset,
    sort: response.sort,
    links: response._links,
    pagination: {
      filter: response.filter,
      sort: response.sort,
      limit: response.limit,
      offset: response.offset,
    },
  };
};

const toCoreResponse = <Dto, Model>(
  response: CoreListEnvelop<Dto>,
  mapper?: (dto: Dto[]) => Model[]
) => createCoreApiResponse(response, mapper);

/**
 * Abstract base class for interacting with Core API resources using CRUD operations.
 *
 * @template Dto - Type of the data transfer object returned from the API
 * @template Post - Type of the data to send to the API when creating/updating resources (defaults to Dto)
 *
 * @example
 * class UsersApiResource extends CoreApiResource<UserDto, UserCreate> {
 *   constructor() {
 *     super('users');
 *   }
 * }
 *
 * const usersApi = new UsersApiResource();
 * usersApi.getAll().subscribe(users => console.log(users));
 */
export abstract class CoreApiResource<Dto, Post = Dto> {
  private http = inject(HttpClient);
  private apiUrl = inject(CORE_API_URL);
  protected constructor(protected readonly uri: string) {}

  /**
   * Creates a new resource.
   *
   * @template Model - The model type to map the response to (defaults to Dto)
   * @param {Post} item - The data to create the new resource with
   * @param {CoreApiMapper<Dto, Model>} [mapper] - Optional function to transform the response data
   * @returns {Observable<Model | Dto>} An observable that emits the created resource
   *
   * @example
   * // Create with default response type
   * resourceService.create({ name: 'New Item' }).subscribe(
   *   result => console.log(result)
   * );
   *
   * @example
   * // Create with custom mapper function
   * resourceService.create(
   *   { name: 'New Item' },
   *   (dto) => new CustomModel(dto)
   * ).subscribe(
   *   model => console.log(model)
   * );
   */
  create(item: Post): Observable<Dto>;

  create<Model = Dto>(item: Post, mapper?: CoreApiMapper<Dto, Model>): Observable<Model>;

  create<Model = Dto>(item: Post, mapper?: CoreApiMapper<Dto, Model>): Observable<Model | Dto> {
    return this.http
      .post<Dto>(this.url, item)
      .pipe(map((response) => mapResponse(response, mapper)));
  }

  /**
   * Updates an existing resource by its ID.
   *
   * @template Model - The model type to map the response to (defaults to Dto)
   * @param {string | number} id - The identifier of the resource to update
   * @param {Omit<Partial<Post> | Post, 'id'>} body - The data to update the resource with (partial or complete, without 'id' field)
   * @param {CoreApiMapper<Dto, Model>} [mapper] - Optional function to transform the response data
   * @returns {Observable<Model | Dto>} An observable that emits the updated resource
   *
   * @example
   * // Update with default response type
   * resourceService.update('123', { name: 'Updated Item' }).subscribe(
   *   result => console.log(result)
   * );
   *
   * @example
   * // Update with custom mapper function
   * resourceService.update(
   *   456,
   *   { name: 'Updated Item', status: 'active' },
   *   (dto) => new CustomModel(dto)
   * ).subscribe(
   *   model => console.log(model)
   * );
   */
  update(
    id: string | number,
    body: Partial<Omit<Post, 'id'>> & Pick<Revision, '_version'>
  ): Observable<Dto>;

  update<Model = Dto>(
    id: string | number,
    body: Partial<Omit<Post, 'id'>> & Pick<Revision, '_version'>,
    mapper?: CoreApiMapper<Dto, Model>
  ): Observable<Dto>;

  update<Model = Dto>(
    id: string | number,
    body: Partial<Omit<Post, 'id'>> & Pick<Revision, '_version'>,
    mapper?: CoreApiMapper<Dto, Model>
  ): Observable<Model | Dto> {
    return this.http
      .patch<Dto>(this.urlWithId(id), body)
      .pipe(map((response) => mapResponse(response, mapper)));
  }

  /**
   * Deletes an existing resource by its ID.
   *
   * @param {string | number} id - The identifier of the resource to delete
   * @returns {Observable<void>} An observable that completes when the deletion is successful
   *
   * @example
   * resourceService.delete('123').subscribe(
   *   () => console.log('Resource deleted successfully')
   * );
   */
  delete(id: string | number): Observable<void> {
    return this.http.delete<void>(this.urlWithId(id));
  }

  /**
   * Retrieves all resources from the API.
   *
   * @template Model - The model type to map the response items to (defaults to Dto)
   * @param {CoreApiListMapper<Dto, Model>} mapper - Function to transform the response data array
   * @returns {Observable<Model[] | Dto[]>} An observable that emits an array of all resources
   *
   * @example
   * // Get all items with default response type
   * resourceService.getAll(items => items).subscribe(
   *   results => console.log(results)
   * );
   *
   * @example
   * // Get all items with custom mapper function
   * resourceService.getAll(
   *   items => items.map(dto => new CustomModel(dto))
   * ).subscribe(
   *   models => console.log(models)
   * );
   */
  public getAll<Model = Dto>(mapper?: CoreApiListMapper<Dto, Model>): Observable<Model[] | Dto[]>;
  public getAll<Model = Dto>(config?: RequestConfig): Observable<Model[] | Dto[]>;
  public getAll<Model = Dto>(
    mapperOrConfig?: CoreApiListMapper<Dto, Model> | RequestConfig,
    config?: RequestConfig
  ): Observable<Model[] | Dto[]>;
  public getAll<Model = Dto>(
    mapperOrConfig?: CoreApiListMapper<Dto, Model> | RequestConfig,
    config?: RequestConfig
  ): Observable<Model[] | Dto[]> {
    const { headers, context, mapper } = this.extractHeadersAndContext<Model>(
      mapperOrConfig,
      config
    );
    return this.http
      .get<CoreListEnvelop<Dto>>(this.url, { headers, context })
      .pipe(map((response) => toCoreResponse(response, mapper).results));
  }

  /**
   * Retrieves all resources from the API, returning the complete response envelope.
   *
   * This method returns the full response envelope that contains additional metadata as
   * pagination, total count, or links. Results are within the _embedded.results property.
   *
   * @returns {Observable<CoreListEnvelop<Dto>>} An observable that emits the complete response envelope
   *
   * @example
   * // Get the complete response envelope
   * resourceService.getAllWithEnvelope().subscribe(
   *   envelope => {
   *     console.log('Results:', envelope_embedded.results);
   *     console.log('Total count:', envelope.totalCount);
   *     // Access other properties in the envelope
   *   }
   * );
   *
   */
  public getAllWithEnvelope(): Observable<CoreListEnvelop<Dto>> {
    return this.http.get<CoreListEnvelop<Dto>>(this.url);
  }

  public getOneById(params: Omit<CoreQueryConfig, 'query'>): Observable<Dto>;

  public getOneById<Model = Dto>(
    params: Omit<CoreQueryConfig, 'query'>,
    mapper?: CoreApiMapper<Dto, Model>
  ): Observable<Model>;

  public getOneById<Model = Dto>(
    params: Omit<CoreQueryConfig, 'query'>,
    mapper?: CoreApiMapper<Dto, Model>
  ): Observable<Model> {
    const { id, headers } = params;
    return this.http
      .get<Dto>(this.urlWithId(id), this.getHeaders(headers))
      .pipe(map((response) => mapResponse(response, mapper)));
  }

  /**
   * Queries and returns resources based on filtering, sorting and pagination criteria.
   *
   * @template Model - The model type to map the response items to (defaults to Dto)
   * @param {CoreListQueryConfig} config - The query configuration object. This should be created using
   * an appropriate instance of FluentFilterBuilder to ensure proper formatting of query parameters.
   * @param {CoreApiListMapper<Dto, Model>} [mapper] - Optional function to transform the response data array
   * @returns {Observable<Model[] | Dto[]>} An observable that emits an array of matching resources
   *
   * @example
   * // Define available filter and sort fields
   * type ProjectAvailableFilter = {
   *   id: '==' | '!=';
   *   status: '==' | '!=';
   *   "user.id": ['!=', '=='];
   *   name: '=@' | '!@';
   * };
   *
   * type ProjectAvailableSort = {
   *    'user.id': 'asc';
   *    status: 'desc' | 'asc';
   *  };
   *
   * const projectQueryBuilder = createQueryBuilder<
   *  ProjectAvailableFilter,
   *  ProjectAvailableSort
   * >;
   *
   * // Query with (optional) mapper function
   *
   * resourceService.query(
   *  projectQueryBuilder()
   *   .equals('status', 'active')
   *   .build(),
   *   (items) => items.map(dto => new ViewModel(dto))
   * ).subscribe(
   *   (models) => console.log(models)
   * );
   */
  public query(config: CoreListQueryConfig): Observable<Dto[]>;
  public query<Model>(
    config: CoreListQueryConfig,
    mapper: CoreApiListMapper<Dto, Model>
  ): Observable<Model[]>;
  public query<Model = Dto>(
    config: CoreListQueryConfig,
    mapper?: CoreApiListMapper<Dto, Model>
  ): Observable<Model[] | Dto[]> {
    return this.getCoreListResponse(config, 'results', mapper);
  }

  public queryWithEnvelope(config: CoreListQueryConfig): Observable<CoreListEnvelop<Dto>> {
    const { query: params, headers, withAccountId, context } = config;
    const url = withAccountId ? `${this.apiUrl}/account/${withAccountId}/${this.uri}` : this.url;
    return this.http.get<CoreListEnvelop<Dto>>(url, {
      params,
      ...this.getHeaders(headers),
      context,
    });
  }

  public queryTotal(config: CoreListQueryConfig): Observable<number> {
    return this.getCoreListResponse(config, 'totalCount');
  }

  public queryPageSize(config: CoreListQueryConfig): Observable<number> {
    return this.getCoreListResponse(config, 'limit');
  }

  public queryPageOffset(config: CoreListQueryConfig): Observable<number> {
    return this.getCoreListResponse(config, 'offset');
  }

  public querySortCriteria(config: CoreListQueryConfig): Observable<string> {
    return this.getCoreListResponse(config, 'sort');
  }

  public queryPaginationMetadata(config: CoreListQueryConfig) {
    return this.getCoreListResponse(config, 'pagination');
  }

  public queryLinks(config: CoreListQueryConfig) {
    return this.getCoreListResponse(config, 'links');
  }

  private getCoreListResponse<K extends keyof CoreApiResponse<Dto, Model>, Model = Dto>(
    config: CoreListQueryConfig,
    property: Exclude<K, 'results'>
  ): Observable<CoreApiListResponseReturnType<Dto, K, Model>>;

  private getCoreListResponse<Model = Dto>(
    config: CoreListQueryConfig,
    property: 'results',
    mapper?: (dto: Dto[]) => Model[]
  ): Observable<Model[] | Dto[]>;

  private getCoreListResponse<K extends keyof CoreApiResponse<Dto, Model>, Model = Dto>(
    config: CoreListQueryConfig,
    property: K,
    mapper?: (dto: Dto[]) => Model[]
  ): Observable<any> {
    return this.queryWithEnvelope(config).pipe(
      map((response) => toCoreResponse(response, mapper)[property])
    );
  }

  private get url(): string {
    return `${this.apiUrl}/${this.uri}`;
  }

  private urlWithId(id: string | number): string {
    return `${this.url}/${id}`;
  }

  private getHeaders(headers?: QueryHeaders): { headers?: HttpHeaders } {
    return { ...getHeaders(headers || {}) };
  }

  private extractHeadersAndContext<Model = Dto>(
    mapperOrConfig?: CoreApiListMapper<Dto, Model> | ListRequestConfig,
    config?: ListRequestConfig
  ): {
    context?: HttpContext;
    headers?: HttpHeaders;
    params?: ListRequestQuery;
    mapper?: CoreApiListMapper<Dto, Model>;
  } {
    const mapper = isMapper(mapperOrConfig) ? mapperOrConfig : undefined;
    const providedConfig = isConfig(mapperOrConfig) ? mapperOrConfig : config || {};

    const { headers, context, query } = providedConfig;
    return {
      mapper,
      params: query,
      ...this.getHeaders(headers),
      ...(context ? { context } : {}),
    };
  }
}

const getHeaders = ({ maxRetry }: QueryHeaders): { headers?: HttpHeaders } => ({
  ...(maxRetry ? { headers: new HttpHeaders({ maxRetry: `${maxRetry}` }) } : {}),
});

const isMapper = <Dto, Model>(
  mapperOrConfig?: CoreApiMapper<Dto, Model> | CoreApiListMapper<Dto, Model> | RequestConfig
): mapperOrConfig is CoreApiMapper<Dto, Model> | CoreApiListMapper<Dto, Model> => {
  return typeof mapperOrConfig === 'function';
};

const isConfig = <Dto, Model>(
  mapperOrConfig?: CoreApiMapper<Dto, Model> | CoreApiListMapper<Dto, Model> | RequestConfig
): mapperOrConfig is RequestConfig => {
  return typeof mapperOrConfig === 'object';
};

const mapResponse = <Dto, Model = Dto>(
  response: Dto,
  mapper?: CoreApiMapper<Dto, Model>
): Model => {
  return mapper ? mapper(response) : (response as unknown as Model);
};
